/**
 * Frontend-only utility to export the currently open AI Tutor conversation
 * as a Word (.docx) document.
 *
 * Enhancements:
 * - Normalizes AI markdown output into readable document structure
 * - Converts markdown tables into real Word tables
 * - Removes placeholder citations like [doc3]
 */

import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
} from "docx"
import type { ChatMessage } from "@/models"

function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

// collapse sequences like "l e g a l" into "legal" (heuristic)
const collapseSpacedLetters = (s: string) => {
    return s.replace(/(?:\b|^)(?:[A-Za-z]\s+){2,}[A-Za-z](?:\b|$)/g, (m) => m.replace(/\s+/g, ""))
}

/**
 * Convert LaTeX math notation to readable text with Unicode symbols
 * Handles nested expressions like fractions and square roots
 */
const convertLatexToUnicode = (text: string): string => {
    // Handle display math $$...$$ and inline math $...$
    return text.replace(/\$\$([\s\S]+?)\$\$|\$(.+?)\$/g, (match, display, inline) => {
        let latex = display || inline

        // Unicode maps for superscript/subscript (defined once, used in loop)
        const SUP_MAP: Record<string, string> = {
            '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
            '+': '⁺', '-': '⁻', '=': '⁼'
        }
        const toSup = (s: string) => s.split('').map((ch) => SUP_MAP[ch] ?? ch).join('')
        const SUB_MAP: Record<string, string> = {
            '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
        }
        const toSub = (s: string) => s.split('').map((ch) => SUB_MAP[ch] ?? ch).join('')

        // Process nested structures multiple times (inside-out)
        // Superscripts/subscripts MUST be inside the loop so that e.g. ^{3} resolves
        // to ³ before the next fraction pass — otherwise \frac{π d^{3}}{6} fails
        // because [^{}]+ can't match content with nested braces.
        for (let i = 0; i < 3; i++) {
            // Greek letters
            latex = latex.replace(/\\alpha\b/g, 'α').replace(/\\beta\b/g, 'β')
                .replace(/\\gamma\b/g, 'γ').replace(/\\delta\b/g, 'δ')
                .replace(/\\epsilon\b/g, 'ε').replace(/\\theta\b/g, 'θ')
                .replace(/\\lambda\b/g, 'λ').replace(/\\mu\b/g, 'μ')
                .replace(/\\pi\b/g, 'π').replace(/\\sigma\b/g, 'σ')
                .replace(/\\tau\b/g, 'τ').replace(/\\phi\b/g, 'φ')
                .replace(/\\omega\b/g, 'ω').replace(/\\Delta\b/g, 'Δ')
                .replace(/\\Sigma\b/g, 'Σ').replace(/\\Omega\b/g, 'Ω')
            
            // Mathematical operators
            latex = latex.replace(/\\times\b/g, '×').replace(/\\div\b/g, '÷')
                .replace(/\\pm\b/g, '±').replace(/\\mp\b/g, '∓')
                .replace(/\\leq\b/g, '≤').replace(/\\geq\b/g, '≥')
                .replace(/\\neq\b/g, '≠').replace(/\\approx\b/g, '≈')
                .replace(/\\equiv\b/g, '≡').replace(/\\infty\b/g, '∞')
                .replace(/\\sum\b/g, '∑').replace(/\\prod\b/g, '∏')
                .replace(/\\int\b/g, '∫').replace(/\\partial\b/g, '∂')
                .replace(/\\nabla\b/g, '∇').replace(/\\cdot\b/g, '·')
                .replace(/\\ldots\b/g, '…').replace(/\\dots\b/g, '…')
            
            // Nested square roots (innermost first)
            latex = latex.replace(/\\sqrt\{([^{}]+)\}/g, '√($1)')
            latex = latex.replace(/\\sqrt\[(\d+)\]\{([^{}]+)\}/g, '$1√($2)')

            // Superscripts (inside loop so ^{3} resolves before next fraction pass)
            latex = latex.replace(/\^{([^}]+)}/g, (_m: string, g: string) => toSup(g))
            latex = latex.replace(/\^(\d+)/g, (_m: string, g: string) => toSup(g))

            // Subscripts (inside loop for same reason)
            latex = latex.replace(/_{([^}]+)}/g, (_m: string, g: string) => toSub(g))
            latex = latex.replace(/_(\d+)/g, (_m: string, g: string) => toSub(g))

            // Nested fractions (innermost first) - handle carefully
            // Support \frac{a}{b}, \dfrac{a}{b}, and common unbraced forms like "\frac a b".
            latex = latex.replace(/\\d?frac\{([^{}]+)\}\{([^{}]+)\}/g, '($1)/($2)')
            latex = latex.replace(/\\d?frac\s*\(?\s*([^\s(){}]+(?:\s[^\s(){}]+)*)\s*\)?\s*\(?\s*([^\s(){}]+(?:\s[^\s(){}]+)*)\s*\)?/g, '($1)/($2)')
            // Catch stray 'frac' or 'dfrac' without leading backslash (robust cleanup)
            latex = latex.replace(/\b(d?frac)\s*\(?\s*([^\s(){}]+(?:\s+[^\s(){}]+)*)\s*\)?\s*\(?\s*([^\s(){}]+(?:\s+[^\s(){}]+)*)\s*\)?/g, '($2)/($3)')

            // Final cleanup: any remaining 'frac' tokens (possibly missing backslashes
            // or braces) — try a loose match: 'frac <numerator> <denominator>' -> '(num)/(den)'
            latex = latex.replace(/\b(?:\\)?d?frac\b\s*([^\s{}()]+(?:\s+[^\s{}()]+)*)\s+([^\s{}()]+(?:\s+[^\s{}()]+)*)/g, '($1)/($2)')
        }
        
        // Remove TeX spacing commands and sizing tokens like \left / \right
        latex = latex.replace(/\\(?:left|right)\b/g, '')
        latex = latex.replace(/\\(?:quad|qquad|,|;|:|!)/g, ' ')

        // Remove remaining backslash-letter commands (e.g., \pi already handled above),
        // but leave punctuation alone (we already removed spacing commands separately).
        latex = latex.replace(/\\([a-zA-Z]+)\b/g, '$1')
        latex = latex.replace(/\{([^}]*)\}/g, '$1')
        latex = latex.replace(/\s+/g, ' ')

        return latex.trim()
    })
}

/**
 * Clean up stray LaTeX tokens that appear OUTSIDE of $...$ delimiters.
 * This catches cases where the AI outputs bare \frac, \pi, etc. without
 * wrapping them in math delimiters.
 */
const cleanupStrayLatex = (text: string): string => {
    let s = text

    // Unicode maps for superscript/subscript
    const SUP_MAP: Record<string, string> = {
        '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
        '+': '⁺', '-': '⁻', '=': '⁼'
    }
    const toSup = (str: string) => str.split('').map((ch) => SUP_MAP[ch] ?? ch).join('')
    const SUB_MAP: Record<string, string> = {
        '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
    }
    const toSub = (str: string) => str.split('').map((ch) => SUB_MAP[ch] ?? ch).join('')

    // Process in a loop: Greek + super/subscripts must resolve BEFORE fractions
    // so that \frac{\pi d^{3}}{6} becomes \frac{π d³}{6} → (π d³)/(6)
    for (let pass = 0; pass < 3; pass++) {
        // Greek letters and math commands
        s = s.replace(/\\pi\b/g, 'π').replace(/\\alpha\b/g, 'α').replace(/\\beta\b/g, 'β')
            .replace(/\\gamma\b/g, 'γ').replace(/\\delta\b/g, 'δ').replace(/\\theta\b/g, 'θ')
            .replace(/\\sigma\b/g, 'σ').replace(/\\mu\b/g, 'μ').replace(/\\lambda\b/g, 'λ')
            .replace(/\\omega\b/g, 'ω').replace(/\\phi\b/g, 'φ').replace(/\\epsilon\b/g, 'ε')
            .replace(/\\tau\b/g, 'τ').replace(/\\Delta\b/g, 'Δ').replace(/\\Sigma\b/g, 'Σ')
            .replace(/\\Omega\b/g, 'Ω').replace(/\\infty\b/g, '∞')
            .replace(/\\times\b/g, '×').replace(/\\div\b/g, '÷')
            .replace(/\\pm\b/g, '±').replace(/\\approx\b/g, '≈')
            .replace(/\\neq\b/g, '≠').replace(/\\leq\b/g, '≤').replace(/\\geq\b/g, '≥')
            .replace(/\\cdot\b/g, '·').replace(/\\sqrt\{([^{}]+)\}/g, '√($1)')

        // Superscripts (before fractions so ^{3} inside frac args resolves first)
        s = s.replace(/\^{([^}]+)}/g, (_m: string, g: string) => toSup(g))
        s = s.replace(/\^(\d+)/g, (_m: string, g: string) => toSup(g))

        // Subscripts
        s = s.replace(/_{([^}]+)}/g, (_m: string, g: string) => toSub(g))
        s = s.replace(/_(\d+)/g, (_m: string, g: string) => toSub(g))

        // Fractions with braces (now inner braces from ^{} are gone)
        s = s.replace(/\\?d?frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, '($1)/($2)')
    }

    // Handle bare "frac" or "dfrac" followed by two tokens
    s = s.replace(/\b(?:\\)?d?frac\s*([^\s{}()]+)\s+([^\s{}()]+)/g, '($1)/($2)')

    // Handle \left, \right, spacing commands
    s = s.replace(/\\(?:left|right)\b/g, '')
    s = s.replace(/\\(?:quad|qquad|,|;|:|!)/g, ' ')

    // Remove remaining \command tokens
    s = s.replace(/\\([a-zA-Z]+)\b/g, '$1')

    // Remove leftover braces
    s = s.replace(/\{([^}]*)\}/g, '$1')

    return s
}

const sanitizeLine = (l: string) => {
    const step1 = String(l)
        // Remove citation placeholders like [doc3]
        .replace(/\[doc\d+\]/g, "")
        .replace(/\[\s*\d+(?:\s*,\s*\d+)+\s*\]/g, "")
        .replace(/\(\s*\d+(?:\s*,\s*\d+)+\s*\)/g, "")
        .replace(/([.!?])\s*\d+(?:\s*,\s*\d+)+(?=\s|$)/g, "$1")
        .replace(/\s+\d+(?:\s*,\s*\d+)+(?=\s|$)/g, "")

    // Convert LaTeX math notation to Unicode (handles $...$ and $$...$$ blocks)
    const step1a = convertLatexToUnicode(step1)

    // Clean up any stray LaTeX tokens that appeared outside of $ delimiters
    const step1b = cleanupStrayLatex(step1a)

    const step2 = step1b.replace(/[\u0000-\u001F\u007F-\u009F\u200B\uFEFF]/g, "")
    const step3 = step2.replace(/\s+/g, " ").trim()
    const step4 = collapseSpacedLetters(step3)
    const step5 = step4
        .replace(/\s+([,.;:!?])/g, "$1")
        .replace(/,\s*\./g, ".")
        .replace(/\s*,\s*$/g, "")
    return step5
}

function normalizeText(raw: string): string[] {
    // Merge multi-line display math blocks ($$...$$) into a single line so
    // `convertLatexToUnicode` (which operates on single-line strings) can
    // detect and convert them. This preserves the $$ delimiters.
    const merged = String(raw).replace(/\$\$([\s\S]*?)\$\$/g, (m) => m.replace(/\r?\n/g, " "))

    return merged
        // Mark markdown headings with a marker to preserve them for rendering
        .replace(/^#{1,6}\s*(.*)$/gm, '___HEADING___$1')
        // Normalize lines
        .split(/\r?\n/)
        .map((l) => sanitizeLine(l))
        .filter((l) => l.length > 0 && !/^\s*-{3,}\s*$/.test(l))
}

// Build TextRuns preserving simple inline markdown like **bold**, *italic*, and `inline code`
function buildRuns(text: string) {
    const runs: TextRun[] = []
    const normalized = text.replace(/\s+([,.;:!?])/g, "$1")
    // split on inline code first
    const parts = normalized.split(/(`[^`]+`)/g)
    for (const part of parts) {
        if (part.startsWith('`') && part.endsWith('`')) {
            const code = part.slice(1, -1)
            runs.push(new TextRun({ text: code, font: 'Courier New' }))
            continue
        }
        // handle bold and italic
        // split further on **bold** and *italic*
        const remaining = part
        const boldItalicRegex = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/g
        let lastIndex = 0
        let m: RegExpExecArray | null
        while ((m = boldItalicRegex.exec(remaining)) !== null) {
            if (m.index > lastIndex) {
                runs.push(new TextRun({ text: remaining.slice(lastIndex, m.index) }))
            }
            if (m[2]) {
                runs.push(new TextRun({ text: m[2], bold: true }))
            } else if (m[4]) {
                runs.push(new TextRun({ text: m[4], italics: true }))
            }
            lastIndex = m.index + m[0].length
        }
        if (lastIndex < remaining.length) {
            runs.push(new TextRun({ text: remaining.slice(lastIndex) }))
        }
    }
    return runs
}

function extractTable(lines: string[], startIndex: number) {
    const rows: string[][] = []
    let i = startIndex

    while (i < lines.length && lines[i].includes("|")) {
        const cells = lines[i]
            .split("|")
            .map((c) => c.trim())
            .filter(Boolean)
        if (!cells.every((c) => /^:?-+:?$/.test(c) || /^:+$/.test(c))) {
            rows.push(cells)
        }
        i++
    }

    return { rows, nextIndex: i }
}

export async function exportConversationToDocx(params: {
    messages: ChatMessage[]
    courseDisplayName?: string | null
    title?: string
}) {
    const { messages, courseDisplayName, title } = params

    const exportable = messages.filter(
        (m) => m.role === "user" || m.role === "assistant" || m.role === "error",
    )

    const now = new Date()
    const children: (Paragraph | Table)[] = []

    // Header
    children.push(
        new Paragraph({
            children: [new TextRun({ text: title ?? "AI Tutor Conversation", bold: true, size: 32 })],
        }),
        new Paragraph(`Course: ${courseDisplayName ?? "N/A"}`),
        new Paragraph(`Exported: ${now.toLocaleString()}`),
        new Paragraph(""),
    )

    for (const m of exportable) {
        const speaker = m.role === "user" ? "You" : m.role === "assistant" ? "AI Tutor" : "Error"

        children.push(
            new Paragraph({
                children: [new TextRun({ text: speaker, bold: true })],
                spacing: { after: 120 },
            }),
        )

        const lines = normalizeText(m.content ?? "")
        let i = 0

        while (i < lines.length) {
            // heading marker (added by normalizeText)
            const headingMatch = lines[i].match(/^___HEADING___(.*)$/)
            if (headingMatch) {
                const headingText = headingMatch[1].trim()
                children.push(
                    new Paragraph({
                        children: [new TextRun({ text: headingText, bold: true })],
                        spacing: { before: 180, after: 120 },
                    }),
                )
                i++
                continue
            }

            // skip separator-only lines
            if (/^\s*-{3,}\s*$/.test(lines[i])) {
                i++
                continue
            }

            // handle fenced code blocks ```
            if (lines[i].trim().startsWith("``")) {
                i++
                const codeLines: string[] = []
                while (i < lines.length && !lines[i].trim().startsWith("``")) {
                    codeLines.push(lines[i])
                    i++
                }
                // skip closing fence
                if (i < lines.length && lines[i].trim().startsWith("``")) i++

                // Add each code line as its own paragraph with monospace font
                codeLines.forEach((cl) => {
                    children.push(
                        new Paragraph({
                            children: [new TextRun({ text: cl, font: "Courier New" })],
                            spacing: { after: 120 },
                        }),
                    )
                })
                continue
            }


            // ASCII table header detection (header line followed by dash separator)
            if (i + 1 < lines.length && /^\s*(-+\s+)+-+\s*$/.test(lines[i + 1])) {
                const headerLine = lines[i].trim()
                const rows: string[][] = []
                const splitRow = (r: string) => r.trim().split(/\s{2,}/).map((c) => c.trim())
                const headerCells = splitRow(headerLine)
                let j = i + 2
                while (j < lines.length && lines[j].trim() !== '') {
                    const cells = splitRow(lines[j])
                    rows.push(cells)
                    j++
                }
                children.push(
                    new Table({
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        rows: [headerCells, ...rows].map(
                            (r, rowIndex) =>
                                new TableRow({
                                    children: r.map((c) =>
                                        new TableCell({
                                            children: [
                                                new Paragraph({
                                                    children:
                                                        rowIndex === 0
                                                            ? [new TextRun({ text: c, bold: true })]
                                                            : buildRuns(c),
                                                }),
                                            ],
                                        }),
                                    ),
                                }),
                        ),
                    }),
                )
                // add an empty paragraph to create spacing after the table
                children.push(new Paragraph({ children: [new TextRun("")], spacing: { after: 160 } }))
                i = j
                continue
            }

            // pipe-delimited table
            if (lines[i].includes("|")) {
                const { rows, nextIndex } = extractTable(lines, i)

                if (rows.length > 0) {
                    children.push(
                        new Table({
                            width: { size: 100, type: WidthType.PERCENTAGE },
                            rows: rows.map(
                                (r, rowIndex) =>
                                    new TableRow({
                                        children: r.map((c) =>
                                            new TableCell({
                                                children: [
                                                    new Paragraph({
                                                        children:
                                                            rowIndex === 0
                                                                ? [new TextRun({ text: c, bold: true })]
                                                                : buildRuns(c),
                                                    }),
                                                ],
                                            }),
                                        ),
                                    }),
                            ),
                        }),
                    )
                    // add an empty paragraph to create spacing after the table
                    children.push(new Paragraph({ children: [new TextRun('')], spacing: { after: 160 } }))
                    i = nextIndex
                    continue
                }
            }

            // list bullet line
            const listMatch = lines[i].match(/^\s*[-*+•]\s+(.*)$/)
            if (listMatch) {
                // preserve inline markdown (bold/italic) so buildRuns can format it
                const content = listMatch[1].trim()
                children.push(new Paragraph({ children: buildRuns(content), bullet: { level: 0 }, spacing: { after: 80 } }))
                i++
                continue
            }

            // inline colon-dash lists like "includes: - item - item"
            const inlineListMatch = lines[i].match(/^(.*?):\s*([\-+•].*)$/s)
            if (inlineListMatch) {
                const prefix = inlineListMatch[1].trim()
                const rest = inlineListMatch[2]
                const items = rest
                    .split(/[\-+•]/)
                    .map((s) => s.replace(/^[\s:\+\-]+/, '').trim())
                    .filter(Boolean)
                children.push(new Paragraph({ children: buildRuns(prefix) }))
                items.forEach((it) => children.push(new Paragraph({ children: buildRuns(it), bullet: { level: 0 }, spacing: { after: 80 } })))
                i++
                continue
            }

            // inline dash list without colon: "Notes - item - item"
            const dashCount = (lines[i].match(/\s-\s/g) || []).length
            if (dashCount >= 2 && !/[=+]/.test(lines[i])) {
                const parts = lines[i].split(/\s-\s/).map((p) => p.trim()).filter(Boolean)
                if (parts.length >= 3) {
                    const [prefix, ...items] = parts
                    children.push(new Paragraph({ children: buildRuns(prefix) }))
                    items.forEach((it) => children.push(new Paragraph({ children: buildRuns(it), bullet: { level: 0 }, spacing: { after: 80 } })))
                    i++
                    continue
                }
            }

            // default paragraph
            // Preserve single hyphens (minus signs) used in equations; only remove stray asterisks
            const sanitized = lines[i].replace(/\*+/g, '').trim()
            children.push(
                new Paragraph({
                    children: buildRuns(sanitized),
                    spacing: { after: 200 },
                }),
            )
            i++
        }
    }

    const doc = new Document({ sections: [{ children }] })
    const blob = await Packer.toBlob(doc)

    const safeCourse = (courseDisplayName ?? "conversation")
        .replace(/[^\w\-]+/g, "_")
        .slice(0, 40)

    downloadBlob(blob, `ai_tutor_${safeCourse}_${now.toISOString().slice(0, 10)}.docx`)
}
